import type { HealthcareService } from "fhir/r3"
import { Button } from "nhsuk-react-components"
import { useNavigate } from "react-router-dom"
import type { SimplifiedAppointment } from "../../../appointment.types"
import { getCancellationQuestionnaireReference, getExtensions } from "../../../fhirUtils/healthcareServiceUtils"
import { InformationBlock } from "../../../components/informationBlock/InformationBlock"

interface Props {
  appointment: SimplifiedAppointment
  healthcareService: HealthcareService
}

const DEFAULT_CANCELLATION_OPTIONS = [
  "Only cancel your appointment if you no longer need it.",
  "You can reschedule your appointment if you still need care.",
  "The booking team will process your cancellation request in approximately 2 working days.",
]

export function CancellationOptions({ appointment, healthcareService }: Props): JSX.Element | null {
  const navigate = useNavigate()

  if (!appointment.canBeCancelled) return null

  const questionnaireId = getCancellationQuestionnaireReference(healthcareService).split("/")[1]

  if (!questionnaireId) return null

  const cancellationInstructionExtension = getExtensions(healthcareService, "CancellationInstructions")

  const cancellationInstructions =
    cancellationInstructionExtension.length > 0
      ? cancellationInstructionExtension.map((instruction) => instruction.valueString || "")
      : DEFAULT_CANCELLATION_OPTIONS

  const onAskToCancelAppointment = () => {
    if (questionnaireId) {
      navigate(`cancellation/${questionnaireId}`, { relative: "path" })
      return
    }
    navigate("cancellation", { relative: "path" })
  }

  return (
    <>
      <InformationBlock header="Cancel your appointment" information={cancellationInstructions} />
      <Button secondary onClick={onAskToCancelAppointment}>
        Ask to cancel appointment
      </Button>
    </>
  )
}
